import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LocalDataTrack } from 'twilio-video';
import ControlPanelHeader from '../ControlPanelHeader/ControlPanelHeader';
import ControlPanelMainControls from '../ControlPanelMainControls/ControlPanelMainControls';
import ControlPanelFooter from '../ControlPanelFooter/ControlPanelFooter';
import './ControlPanel.scss';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import { TaskType } from '../../commands/practitioner/enums/TaskType';
import BackendService from '../../services/BackendService';
import { startTimer, endTimer, metrics, mediaKey, taskKey, callKey } from '../../metrics/sentryMetrics';
import ControlPanelSessionTab from '../ControlPanelSessionTab/ControlPanelSessionTab';
import ControlPanelTaskTab from '../ControlPanelTaskTab/ControlPanelTaskTab';
import { useTranslation } from 'react-i18next';
import { SphereTrajectoryOption } from '../../commands/practitioner/enums/SphereTrajectoryOption';
import { CommandPrefixes } from '../../commands/practitioner/enums/CommandPrefixes';
import { Beforeunload } from 'react-beforeunload';
import moment from 'moment';
import { State } from '../../commands/practitioner/enums/State';
import { MediaStates } from '../../commands/practitioner/enums/MediaStates';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import { MediaInteractions } from '../../commands/practitioner/enums/MediaInteractions';
import { config } from '../PhaserApp/components/application/config/TherapistSceneConfig';
import TaskScene from '../PhaserApp/components/application/scene/TaskScene';
import { SourceProjection } from '../../commands/practitioner/enums/SourceProjection';
import useMediaContext from '../../hooks/useMediaContextHook/useMediaContext';
import TabOptionsToogle from '../TabOptionsToogle/TabOptionsToogle';
import { Calculation } from '../PhaserApp/enums/CalculationSettings';
import {
  commandVersion1_2_0,
  isVersionLessThan,
  commandVersion1_3_0,
  commandVersion1_5_0,
} from '../../utils/versionCheck';
import { ColorSwitchOptions } from '../../commands/practitioner/enums/ColorSwitchOptions';
import { StroopConfigurationOptions } from '../../commands/practitioner/enums/StroopConfigurationOptions';
import { CLIENT_PAGE_PRESENCE_TIMEOUT_MS, isClientPagePresenceUri } from '../../utils/consts';

export function getParameterByName(name: string, url: string = '') {
  if (!url) url = window.location.href;
  name = name.replace(/[\[\]]/g, '\\$&');
  let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const addComma = (value: any) => {
  return `${value},`;
};

const addSeed = (value: any) => {
  return `${value}`;
};

const SESSION_TAB = 'Sessielog';
const TASK_TAB = 'Taakcomponenten';

interface ContainerProps {
  ConnectToRoom: (token: string, track: LocalDataTrack) => void | Promise<void>;
  StopCall: () => void;
  location?: any;
  practitionerIsConnected: boolean;
  isNetworkReconnecting: boolean;
  setPractitionerIsConnected: Dispatch<SetStateAction<boolean>>;
  currentState: any;
  isCallActive: boolean;

  clientIsConnected: boolean;
  setClientIsConnected: Dispatch<SetStateAction<boolean>>;
  setWaitingForClient: Dispatch<SetStateAction<boolean>>;
  toggleCallHasStarted: () => void | Promise<void>;
  token: string;
  setToken: Dispatch<SetStateAction<string>>;
  callAbortSignal: number;
  userStoppedCall: boolean;
  onRecallRequired: () => void;
  onUserStoppedCall: () => void;
  registerStopCallHandler?: (handler: () => void) => void;

  mediaPreviewPortalNode: HTMLDivElement | null;
  cameraPortalNode: HTMLDivElement | null;
  gamePortalNode: HTMLDivElement | null;
  callButtonHostEl: HTMLDivElement | null;
  inCallButtonHostEl?: HTMLDivElement | null;
  onClientPageReadyChange?: (isReady: boolean) => void;
  canStartCall?: boolean;
}

const ControlPanel: React.FC<ContainerProps> = ({
  ConnectToRoom,
  StopCall,
  practitionerIsConnected,
  isNetworkReconnecting,
  setPractitionerIsConnected,
  currentState,
  isCallActive,

  setWaitingForClient,
  toggleCallHasStarted,
  token,
  setToken,
  callAbortSignal,
  userStoppedCall,
  onRecallRequired,
  onUserStoppedCall,
  registerStopCallHandler,

  mediaPreviewPortalNode,
  cameraPortalNode,
  gamePortalNode,
  callButtonHostEl,
  inCallButtonHostEl,
  onClientPageReadyChange,
  canStartCall = true,
}) => {
  const { t } = useTranslation();
  const { isConnected: isDeviceConnected, waitingForDeviceCheck } = currentState || {};
  const isWaitingForDeviceCheck = waitingForDeviceCheck === true || currentState?.isWaitingForDeviceCheck === true;
  const {
    eyeMovement,
    speed,
    trajectory,
    interactive,
    autoSpeed,
    setAutoSpeed,
    reactionVisual,
    reactionAudio,
    colorInterval,
    calculationDifficulty,
    sound,
    soundClicks,
    soundPlops,
    soundBuzzer,
    notes,
    vocScores,
    sudScores,
    setIsPractitionerReactPlayerStarted: setMediaIsStarted,
    taskHasStarted,
    setTaskHasStarted,
    setGame,
    stroopConfiguration,
    reactionAnimation,
    theme,
  } = usePractitionerControlsContext();

  const { sessionId, treatmentId, hasDevice } = useAppContext();

  const version = typeof currentState?.version === 'string' ? currentState.version : undefined;
  const isDeviceThemeSupported = hasDevice && !!version && !isVersionLessThan(version, commandVersion1_5_0);
  const shouldShowThemeSelector = !hasDevice || isDeviceThemeSupported;
  const shouldSendThemeToBackend = !hasDevice || isDeviceThemeSupported;

  // Tabs
  const tabOptions = [TASK_TAB, SESSION_TAB];
  const [activeTab, setActiveTab] = useState('Taakcomponenten');

  // Room properties
  const roomState = useRoomState();
  const isConnected = roomState !== 'disconnected';
  const [clientIsConnected, setClientIsConnected] = useState(false);
  const [clientPageIsReady, setClientPageIsReady] = useState(false);
  useEffect(() => {
    onClientPageReadyChange?.(clientPageIsReady);
  }, [clientPageIsReady, onClientPageReadyChange]);
  const [clientPagePresenceVersion, setClientPagePresenceVersion] = useState(0);
  const [clientPagePresenceUpdatedAt, setClientPagePresenceUpdatedAt] = useState(0);
  const clientPagePresenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPassivePresenceCommandRef = useRef<string | null>(null);
  const useLegacy = hasDevice && isVersionLessThan(currentState.version ?? '0.0.0', commandVersion1_3_0);
  const handleClick = (option: string) => {
    setActiveTab(option);
  };

  function getRandomInt(max: number) {
    return Math.floor(Math.random() * max);
  }

  const [corrects, setCorrects] = useState(0);
  const [faults, setFaults] = useState(0);
  const [countersHostEl, setCountersHostEl] = useState<HTMLDivElement | null>(null);
  const [initialResults, setInitialResults] = useState([] as string[]);
  const [totalResults, setTotalResults] = useState(() => [] as string[]);
  const [taskStartedOnce, setTaskStartedOnce] = useState<boolean>(false);
  const [gameIsLoading, setGameIsLoading] = useState<boolean>(true);
  // Command settings
  const [taskSnapoutClicked, setTaskSnapoutClicked] = useState<boolean>(false);
  const isReactionTask =
    reactionVisual || reactionAudio || calculationDifficulty !== 0 || stroopConfiguration !== 0 || reactionAnimation;
  // therapist.calledBy = 'therapist'
  //phaserGame
  const phaserRef = useRef<Phaser.Game | null>(null);
  const maxBallSpeed = 2;
  const speedMultiplier = 1.75;
  const prevLengthRef = useRef<number>(0);
  const firstRender = useRef(false);

  const isOldVersion =
    typeof currentState.version === 'string' && isVersionLessThan(currentState.version, commandVersion1_2_0);
  const isFullCommandToSend = !!currentState.version && !isVersionLessThan(currentState.version, commandVersion1_3_0);
  const commandToSend = useMemo(() => {
    // Create the command based on all settings.
    let value = '';

    //Should I add command here?

    value += addComma(taskHasStarted ? TaskType.Enabled : TaskType.Disabled);
    value += addComma(taskSnapoutClicked ? State.ON : eyeMovement ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? maxBallSpeed * speedMultiplier : speed * speedMultiplier);
    value += addComma(taskSnapoutClicked ? SphereTrajectoryOption.Bouncing : trajectory);
    value += addComma(taskSnapoutClicked ? State.ON : interactive ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.OFF : autoSpeed ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : reactionVisual ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : reactionAudio ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? ColorSwitchOptions.Automatically : colorInterval);
    value += addComma(taskSnapoutClicked ? Calculation.easy : calculationDifficulty);
    value += addComma(taskSnapoutClicked ? State.ON : sound ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : soundClicks ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? State.ON : soundPlops ? State.ON : State.OFF);
    value += addComma(taskSnapoutClicked ? 5 : soundBuzzer);
    if (hasDevice && isFullCommandToSend) {
      value += addComma(taskSnapoutClicked ? State.ON : reactionAnimation ? State.ON : State.OFF);
      value += addComma(stroopConfiguration);
    } else if (hasDevice && !isFullCommandToSend) {
      value += addComma(State.OFF);
      value += addComma(StroopConfigurationOptions.None);
    } else {
      value += addComma(taskSnapoutClicked ? State.ON : reactionAnimation ? State.ON : State.OFF);
      value += addComma(taskSnapoutClicked ? StroopConfigurationOptions.None : stroopConfiguration);
    }
    value += addSeed(getRandomInt(1_000_000));

    return value;
  }, [
    taskHasStarted,
    eyeMovement,
    speed,
    trajectory,
    interactive,
    autoSpeed,
    reactionVisual,
    reactionAudio,
    colorInterval,
    calculationDifficulty,
    sound,
    soundClicks,
    soundPlops,
    soundBuzzer,
    taskSnapoutClicked,
    stroopConfiguration,
    reactionAnimation,
    isFullCommandToSend,
    hasDevice,
  ]);
  const { setSelectedMarket } = useMediaContext();

  const clearClientPagePresenceTimeout = useCallback(() => {
    if (clientPagePresenceTimeoutRef.current !== null) {
      clearTimeout(clientPagePresenceTimeoutRef.current);
      clientPagePresenceTimeoutRef.current = null;
    }
  }, []);

  const markClientPageAsReady = useCallback(() => {
    setClientPageIsReady(true);
    setClientPagePresenceVersion((v) => v + 1);
    setClientPagePresenceUpdatedAt(Date.now());
    clearClientPagePresenceTimeout();
    clientPagePresenceTimeoutRef.current = setTimeout(() => {
      setClientPageIsReady(false);
      clientPagePresenceTimeoutRef.current = null;
    }, CLIENT_PAGE_PRESENCE_TIMEOUT_MS);
  }, [clearClientPagePresenceTimeout]);

  const markClientPageAsUnavailable = useCallback(() => {
    setClientPageIsReady(false);
    clearClientPagePresenceTimeout();
  }, [clearClientPagePresenceTimeout]);

  const handleClientPagePresenceStatus = useCallback(
    (command: string | undefined | null, allowDuplicate = false) => {
      if (typeof command !== 'string' || !command.startsWith('MLSR:')) {
        return;
      }

      try {
        const [, payload] = command.split('MLSR:');
        const parts = payload.split(',');
        const statusNum = Number(parts[0]);
        const rawUri = parts.slice(2).join(',');
        const m = rawUri.match(/"(.+)"/);
        const uri = decodeURIComponent(m ? m[1] : rawUri.replace(/"/g, ''));

        if (!isClientPagePresenceUri(uri)) {
          return;
        }

        if (!allowDuplicate && command === lastPassivePresenceCommandRef.current) {
          return;
        }
        if (!allowDuplicate) {
          lastPassivePresenceCommandRef.current = command;
        }

        if (statusNum === MediaStates.Successful) {
          markClientPageAsReady();
          return;
        }

        if (statusNum === MediaStates.Failure) {
          markClientPageAsUnavailable();
        }
      } catch {
        // Ignore malformed status reports.
      }
    },
    [markClientPageAsReady, markClientPageAsUnavailable]
  );

  useEffect(() => {
    return () => {
      clearClientPagePresenceTimeout();
    };
  }, [clearClientPagePresenceTimeout]);

  useEffect(() => {
    const firstLoadMarket = localStorage.getItem('i18nextLng');

    if (firstLoadMarket === 'en') {
      setSelectedMarket('nl');
    } else {
      if (firstLoadMarket) setSelectedMarket(firstLoadMarket);
    }
  }, []);

  useEffect(() => {
    if (!firstRender.current) {
      firstRender.current = true;
      return;
    } else {
      let booleanTheme = !!theme;

      const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
      therapist.updateTaskBasedOnEncodedCommand(
        BackendService.generateEscapedTaskSettingsCommandString(commandToSend),
        booleanTheme
      );
      if (shouldSendThemeToBackend) {
        BackendService.sendThemeCommand(sessionId, treatmentId, booleanTheme);
      }
    }
  }, [theme]);

  useEffect(() => {
    if (!taskStartedOnce) return;
    if (!phaserRef.current) {
      console.warn('Game not initialized yet. Skipping task start/stop.');
      return;
    }

    const booleanTheme = !!theme;
    const therapist = phaserRef.current.scene.keys.therapist as TaskScene;
    therapist.updateTaskBasedOnEncodedCommand(
      BackendService.generateEscapedTaskSettingsCommandString(commandToSend),
      booleanTheme,
      useLegacy
    );

    (async () => {
      try {
        await BackendService.sendTaskSettingsCommandToClient(sessionId, treatmentId, commandToSend);
      } catch (err) {
        console.error('Failed to sync task settings and theme', err);
      }
    })();
  }, [commandToSend, taskStartedOnce, sessionId, treatmentId, useLegacy]);

  useEffect(() => {
    if (!phaserRef.current) {
      console.warn('Game not initialized yet. Skipping theme update.');
      return;
    }
    const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
    if (therapist === undefined) return;
    if (taskHasStarted) {
      therapist.startTask();
    } else if (taskStartedOnce) {
      therapist.stopTask();
    }
  }, [taskHasStarted, phaserRef.current]);

  const handleToggleTaskClicked = () => {
    setTaskSnapoutClicked(false);
    const newStartedTaskValue = !taskHasStarted;
    if (newStartedTaskValue) {
      // Start E2E timer; client will ack via MLSR task://start
      try {
        startTimer(taskKey());
      } catch {}
      setTaskStartedOnce(true);
      // When a task is started on the device, the media should be paused for the practitioner
      if (hasDevice && isOldVersion) {
        setMediaIsStarted(false);
      }
    }
    setTaskHasStarted(newStartedTaskValue);
    setInitialResults(totalResults);
  };

  const handleSnapoutClicked = () => {
    setTaskSnapoutClicked(!taskSnapoutClicked);
  };

  const parseFeedbackResults = (result: string[]) => {
    const allFeedback = (result || []).filter((reactionFeedback: string) =>
      reactionFeedback.includes(CommandPrefixes.REACTION_FEEDBACK)
    );
    let correctsValue = 0;
    let faultsValue = 0;
    allFeedback.forEach((feedback) => {
      if (feedback.includes('1')) {
        correctsValue++;
      } else {
        faultsValue++;
      }
    });
    setCorrects(correctsValue);
    setFaults(faultsValue);
  };

  useEffect(() => {
    const onLoad = () => setGameIsLoading(false);

    if (document.readyState === 'complete') {
      onLoad();
    } else {
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  useEffect(() => {
    if (!gameIsLoading) {
      phaserRef.current = new Phaser.Game(config);
      setGame(phaserRef.current);
    }
  }, [gameIsLoading]);

  useEffect(() => {
    // track where we left off for APD‐type commands
    let indexLastHandledCommand = -1;
    let firstCall = true;

    // the polling function
    const pollCommands = async () => {
      if (!phaserRef.current) {
        console.warn('Polling skipped: Game not initialized');
        return;
      }
      try {
        const result = await BackendService.fetchCurrentCommandsForPractitioner(sessionId, treatmentId);
        if (!Array.isArray(result)) {
          return;
        }
        // Update the total number of reactions we received
        setTotalResults(result);

        if (result.length === 0) {
          prevLengthRef.current = 0;
          return;
        }

        const lastIdx = result.length - 1;
        const lastCommand = result[lastIdx];

        if (typeof lastCommand !== 'string') {
          prevLengthRef.current = result.length;
          return;
        }

        if (firstCall) {
          indexLastHandledCommand = result.length;
          firstCall = false;
        }

        if (result.length > prevLengthRef.current) {
          const nextCommands = result.slice(prevLengthRef.current);
          nextCommands.forEach((command: string) => {
            handleClientPagePresenceStatus(command, true);
          });
        }

        // Some backends keep command list size stable while rotating content.
        // Ensure the latest MLSR presence signal is still observed in that case.
        handleClientPagePresenceStatus(lastCommand, false);

        if (lastCommand.startsWith('TC:')) {
          const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
        }

        if (lastCommand.startsWith('RPF')) {
          let booleanTpeme = !!theme;
          const therapist = phaserRef?.current?.scene.keys.therapist as TaskScene;
          therapist.updateTaskBasedOnEncodedCommand(lastCommand, booleanTpeme);

          if (result.length > prevLengthRef.current) {
            therapist.ClientToPractitionerTaskAnswer(lastCommand);
          }
        }

        // Look for load-status reports to finalize timers (media/task/call)
        // Format: MLSR:<status>,<progress>,"<uri>"
        if (lastCommand.startsWith('MLSR:')) {
          try {
            const [, payload] = lastCommand.split('MLSR:');
            const parts = payload.split(',');
            const statusNum = Number(parts[0]);
            const rawUri = parts.slice(2).join(',');
            const m = rawUri.match(/"(.+)"/);
            const uri = decodeURIComponent(m ? m[1] : rawUri.replace(/"/g, ''));

            if (!isClientPagePresenceUri(uri)) {
              const status = statusNum === 1 ? 'successful' : statusNum === 2 ? 'failure' : String(statusNum);

              // media play E2E timing keyed by URI
              const finishedMedia = endTimer(mediaKey(uri), 'media.play.propagation_ms', {
                status,
                sessionId,
                treatmentId,
              });

              // task and call acknowledgements use special URIs
              if (uri.startsWith('task://start')) {
                endTimer(taskKey(), 'task.start.propagation_ms', { status, sessionId, treatmentId });
              }
              if (uri.startsWith('call://connected')) {
                endTimer(callKey(), 'call.connect.propagation_ms', { status, sessionId, treatmentId });
              }

              // Also emit a simple counter for status categories
              metrics.count('mlsr.received', 1, {
                status,
                uri_type: uri.split('://')[0] ?? 'media',
                sessionId,
                treatmentId,
              });
            }
          } catch (e) {
            // ignore parse errors
          }
        }

        // remember how many we’ve seen so far
        prevLengthRef.current = result.length;
      } catch (e) {
        console.error(e);
      }
    };

    const intervalId = window.setInterval(() => {
      void pollCommands();
    }, 300);

    return () => {
      clearInterval(intervalId);
    };
  }, [theme]);

  useEffect(() => {
    if (!taskHasStarted) {
      setInitialResults(totalResults);
    }
  }, [totalResults, taskHasStarted, setInitialResults]);

  useEffect(() => {
    const startLengthToSlice = initialResults.length;
    const endLengthToSlice = totalResults.length;
    let arrayToParse = totalResults.slice(startLengthToSlice, endLengthToSlice);
    parseFeedbackResults(arrayToParse);
  }, [initialResults, totalResults]);

  useEffect(() => {
    setCorrects(0);
    setFaults(0);
  }, [taskHasStarted]);

  const combinedAttributesToSend = useMemo(() => {
    return {
      session_completed: '1', // Hardcoded toggle the correct prop
      notes: [notes],
      sud_array: sudScores.map((item: any) => item.value.toString()),
      validity_of_cognition_array: vocScores.map((item: any) => item.value.toString()),
    };
  }, [notes, vocScores, sudScores]);

  const parseUmirCommands = (commands: string[]) => {
    const umirCommands = commands.filter((command: string) =>
      command.includes(CommandPrefixes.USER_INTERACTION_MEDIA_REPORT)
    );
    const commandToUse = (umirCommands || [])[0];
    if (!commandToUse || !hasDevice) {
      return;
    }
    if (commandToUse.includes(MediaInteractions.Stopped.toString())) {
      setMediaIsStarted(false);
    } else if (commandToUse.includes(MediaInteractions.Resumed.toString())) {
      setMediaIsStarted(true);
    }
  };

  useEffect(() => {
    const startLengthToSlice = initialResults.length;
    const endLengthToSlice = totalResults.length;
    let arrayToParse = totalResults.slice(startLengthToSlice, endLengthToSlice);
    parseUmirCommands(arrayToParse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialResults, totalResults]);

  // @ts-ignore
  const triggerCloseOnUnity = useCallback(
    (e: BeforeUnloadEvent) => {
      try {
        setTaskHasStarted(false);
        const endDate = moment().utc().toISOString();
        BackendService.updateSessionData(sessionId, treatmentId, combinedAttributesToSend, endDate)
          .then(async () => {
            try {
              await BackendService.sendLoadMedia(
                sessionId,
                treatmentId,
                '',
                MediaTypes.VIDEO,
                SourceProjection.Flat,
                'Off'
              );
              await BackendService.sendTaskSettingsCommandToClient(sessionId, treatmentId, commandToSend);
              await BackendService.sendMediaVisibility(sessionId, treatmentId, '', false);
            } catch (err) {
              console.error('Failed to reset the media and clientState.');
            }
          })
          .catch((error) => {
            console.error('Failed to send last update session data and ending the session.', error);
          });
      } catch (errorValue) {
        console.error('failed to update the session data', errorValue);
      } finally {
        e.preventDefault();
        return 'Are you sure you wish to close this app?';
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notes, sudScores, vocScores, combinedAttributesToSend]
  );

  const renderCorrectTab = useMemo(() => {
    switch (activeTab) {
      case SESSION_TAB:
        return <ControlPanelSessionTab sessionUUID={sessionId} treatmentUUID={treatmentId} />;
      case TASK_TAB:
      default:
        return <ControlPanelTaskTab sessionUUID={sessionId} treatmentUUID={treatmentId} />;
    }
  }, [activeTab, sessionId, treatmentId]);

  useEffect(() => {
    if (hasDevice && !isDeviceConnected) {
      setMediaIsStarted(false);
    }
  }, [isDeviceConnected, hasDevice, setMediaIsStarted]);

  const sendThemeCommand = async (value: boolean) => {
    if (!shouldSendThemeToBackend) {
      return;
    }
    try {
      await BackendService.sendThemeCommand(sessionId, treatmentId, value);
    } catch (e) {
      console.log('failed to send themeCommand');
    }
  };

  return (
    <div className="container">
      <Beforeunload onBeforeunload={triggerCloseOnUnity} />
      <ControlPanelHeader
        callHasStarted={isConnected}
        practitionerIsConnected={practitionerIsConnected}
        isNetworkReconnecting={isNetworkReconnecting}
        clientIsConnected={clientIsConnected}
        clientPageIsReady={clientPageIsReady}
        clientPagePresenceVersion={clientPagePresenceVersion}
        clientPagePresenceUpdatedAt={clientPagePresenceUpdatedAt}
        setClientIsConnected={setClientIsConnected}
        setPractitionerIsConnected={setPractitionerIsConnected}
        treatmentId={treatmentId}
        sessionId={sessionId}
        connectToRoom={ConnectToRoom}
        StopCall={StopCall}
        currentState={currentState}
        showThemeSelector={shouldShowThemeSelector}
        setWaitingForClient={setWaitingForClient}
        toggleCallHasStarted={toggleCallHasStarted}
        token={token}
        setToken={setToken}
        callAbortSignal={callAbortSignal}
        userStoppedCall={userStoppedCall}
        onRecallRequired={onRecallRequired}
        onUserStoppedCall={onUserStoppedCall}
        registerStopCallHandler={registerStopCallHandler}
        callButtonHostEl={callButtonHostEl}
        inCallButtonHostEl={inCallButtonHostEl}
        setCountersHostEl={setCountersHostEl}
        canStartCall={canStartCall}
      />

      <ControlPanelMainControls
        taskSnapoutClicked={taskSnapoutClicked}
        taskIsStarted={taskHasStarted}
        corrects={corrects}
        faults={faults}
        setTaskSnapoutClicked={handleSnapoutClicked}
        toggleIsStarted={handleToggleTaskClicked}
        isReactionTask={isReactionTask}
        autospeed={autoSpeed}
        setAutoSpeed={setAutoSpeed}
        countersHostEl={countersHostEl}
      />
      <div>
        <TabOptionsToogle onChangeHandler={handleClick} isActive={activeTab} />
      </div>
      <div className="tabs-container">{renderCorrectTab}</div>
      <ControlPanelFooter
        isCallActive={isCallActive}
        isGameLoading={gameIsLoading}
        sendThemeCommand={sendThemeCommand}
        mediaPreviewPortalNode={mediaPreviewPortalNode}
        cameraPortalNode={cameraPortalNode}
        gamePortalNode={gamePortalNode}
      />
    </div>
  );
};

export default ControlPanel;
